/**
 * SegmentsTabParts — toolbar and empty-state subcomponents for SegmentsTab.
 */

import { LayoutGrid, LayoutList, Plus } from 'lucide-react'
import type { SegmentsView } from '@/stores/marketSegmentationViewStore'

interface ToolbarProps {
  view: SegmentsView
  onViewChange: (v: SegmentsView) => void
  onAddClick: () => void
  showAddButton: boolean
  segmentCount: number
}

export function SegmentsToolbar({
  view,
  onViewChange,
  onAddClick,
  showAddButton,
  segmentCount,
}: ToolbarProps) {
  return (
    <div
      className="flex shrink-0 items-center justify-between border-b px-6 py-3"
      style={{ borderColor: 'var(--color-border-subtle)', backgroundColor: 'var(--color-warm-50)' }}
    >
      <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {segmentCount} {segmentCount === 1 ? 'segment' : 'segments'}
      </div>
      <div className="flex items-center gap-2">
        <div
          role="group"
          aria-label="View mode"
          className="inline-flex items-center gap-0.5 rounded-[8px] border p-[3px]"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'white' }}
        >
          <ViewToggleButton active={view === 'list'} onClick={() => onViewChange('list')} label="List">
            <LayoutList className="h-3.5 w-3.5" />
          </ViewToggleButton>
          <ViewToggleButton active={view === 'cards'} onClick={() => onViewChange('cards')} label="Cards">
            <LayoutGrid className="h-3.5 w-3.5" />
          </ViewToggleButton>
        </div>
        {showAddButton ? (
          <button
            type="button"
            onClick={onAddClick}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--color-text)' }}
          >
            <Plus className="h-3 w-3" />
            Add segment
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ViewToggleButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${label} view`}
      className="inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-[12px] font-medium transition-colors"
      style={{
        backgroundColor: active ? 'var(--color-warm-100)' : 'transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
      }}
    >
      {children}
      {label}
    </button>
  )
}

export function ListEmptyState({ onAddClick }: { onAddClick: () => void }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div
          className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--color-warm-100)' }}
        >
          <LayoutList className="h-6 w-6" style={{ color: 'var(--color-text-muted)' }} />
        </div>
        <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
          No segments yet
        </h3>
        <p
          className="mx-auto mt-1.5 max-w-sm text-xs"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Add your first market segment. Start with the one you know best — you can iterate as
          you learn, and the AI can fill in sizing/scoring later.
        </p>
        <button
          type="button"
          onClick={onAddClick}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--color-market)' }}
        >
          <Plus className="h-3 w-3" />
          Add first segment
        </button>
      </div>
    </div>
  )
}
